import { UserInfoModel } from 'app/models/userInfoModel'
import { RolePermissionModel } from 'app/models/rolePermissionModel'
import { UserWithRolesModel } from 'app/models/userWIthRolesModel'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import {
  CompanyEffectivePermission,
  effectiveRolesInCompany,
} from '@/modules/shared/types/account'
import { flatten } from 'lodash'

// Backed by an in-memory module cache (not localStorage). UserProvider hydrates
// it from the /config-api/user + /config-api/role TanStack queries on mount,
// so a hard refresh always refetches — no stale auth state across sessions.
let cachedUserInfo: UserInfoModel | null = null
let cachedRoles: Array<RolePermissionModel> | null = null
// The server's per-company capability projection — the source of truth for getRolesInCompany.
let cachedEffectivePermissions: Array<CompanyEffectivePermission> | null = null

export const saveUserInfo = (
  userInfo: UserInfoModel,
  roles: Array<RolePermissionModel>,
  companies: Array<CompanyDetailsModel>,
  effectivePermissions?: Array<CompanyEffectivePermission>,
): UserInfoModel => {
  let newUserInfo = populateUserInfoWithRolesFromTenant(userInfo, companies)
  newUserInfo = populateUserInfoWithGroupRoles(userInfo, companies)

  cachedUserInfo = newUserInfo
  cachedRoles = roles
  if (effectivePermissions != null) {
    cachedEffectivePermissions = effectivePermissions
  }

  return newUserInfo
}

export const getUserInfo = (): UserWithRolesModel => {
  return cachedUserInfo != null
    ? { userInfo: cachedUserInfo, roles: cachedRoles ?? [] }
    : null
}

export const isAdmin = () => {
  const userWithRoles = getUserInfo()
  return userWithRoles != null && userWithRoles.userInfo.globalAdmin
}

export const isPaymentAdmin = () => {
  const userWithRoles = getUserInfo()
  return userWithRoles != null && userWithRoles.userInfo.paymentAdmin
}

export const isCompanyAdmin = () => {
  const userWithRoles = getUserInfo()
  return userWithRoles != null && userWithRoles.userInfo.companyAdmin
}

export const isDemoCompanyAdmin = () => {
  const userWithRoles = getUserInfo()
  return userWithRoles != null && userWithRoles.userInfo.demoCompanyAdmin
}

export const isDriver = (companyId: string) => {
  const rolesInCompany = getRolesInCompany(companyId)

  const isDriver =
    !isAdmin() &&
    !isPaymentAdmin() &&
    !isCompanyAdmin() &&
    !isDemoCompanyAdmin() &&
    rolesInCompany.some((r) => r.isDriver)
  return isDriver
}

export const isManager = (companyId: string) => {
  const rolesInCompany = getRolesInCompany(companyId)

  const isManager =
    !isAdmin() &&
    !isPaymentAdmin() &&
    !isCompanyAdmin() &&
    !isDemoCompanyAdmin() &&
    rolesInCompany.some(
      (r) =>
        r.canEditCompany ||
        r.canCreateCompany ||
        r.canRemoveCompany ||
        r.canEditUser ||
        r.canCreateUser ||
        r.canRemoveUser,
    )
  return isManager
}

// Effective permissions for the company, from the server projection. See effectiveRolesInCompany.
export const getRolesInCompany = (
  companyId: string,
): Array<RolePermissionModel> =>
  effectiveRolesInCompany(cachedEffectivePermissions, companyId) as RolePermissionModel[]

export const clearUserInfo = () => {
  cachedUserInfo = null
  cachedRoles = null
  cachedEffectivePermissions = null
}

const populateUserInfoWithGroupRoles = (
  userInfo: UserInfoModel,
  companies: CompanyDetailsModel[],
) => {
  const newUserInfo = { ...userInfo }
  if (companies != null) {
    for (const company of companies.filter(
      (c) =>
        (c.groups != null && c.groups.length > 0) ||
        (c.tenant && c.tenant.groups != null && c.tenant.groups.length > 0),
    )) {
      let existingCompany = newUserInfo.roles.find(
        (r) => r.company.id == company.id,
      )
      if (existingCompany == null) {
        existingCompany = {
          company: {
            id: company.id,
            name: company.companyName,
            roles: [],
          },
        }

        newUserInfo.roles.push(existingCompany)
      }

      if (company.groups) {
        for (const group of company.groups) {
          const groupWithRoles = userInfo.groups.find(
            (g) => g.group.id == group.id,
          )
          if (groupWithRoles != null) {
            for (const groupRole of groupWithRoles.roles) {
              if (
                !existingCompany.company.roles.find((r) => r.id == groupRole.id)
              ) {
                existingCompany.company.roles.push(groupRole)
              }
            }
          }
        }
      }
      if (company.tenant && company.tenant.groups) {
        for (const group of company.tenant.groups) {
          const groupWithRoles = userInfo.groups.find(
            (g) => g.group.id == group.id,
          )
          if (groupWithRoles != null) {
            for (const groupRole of groupWithRoles.roles) {
              if (
                !existingCompany.company.roles.find((r) => r.id == groupRole.id)
              ) {
                existingCompany.company.roles.push(groupRole)
              }
            }
          }
        }
      }
    }
  }
  return newUserInfo
}

const populateUserInfoWithRolesFromTenant = (
  userInfo: UserInfoModel,
  companies: CompanyDetailsModel[],
) => {
  const newUserInfo = { ...userInfo }
  if (companies != null) {
    const companiesWithTenant = flatten(
      companies.filter((c) => c.childCompanies).map((x) => x.childCompanies),
    )
    for (const company of companiesWithTenant.filter((c) => c.tenant != null)) {
      const tenantCompany = newUserInfo.roles.find(
        (r) => r.company.id == company.tenant.id,
      )
      if (tenantCompany != null) {
        let currentRoles = newUserInfo.roles.find(
          (r) => r.company.id == company.id,
        )
        if (currentRoles == null) {
          currentRoles = {
            company: {
              id: company.id,
              name: company.companyName,
              roles: [],
            },
          }

          newUserInfo.roles.push(currentRoles)
        }

        for (const role of tenantCompany.company.roles) {
          if (
            currentRoles.company.roles.find((cr) => cr.id == role.id) == null
          ) {
            currentRoles.company.roles.push(role)
          }
        }
      }
    }
  }
  return newUserInfo
}
